import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

/**
 * ARMNEXT cart drawer (CART-4): trash button on a grouped Kaching bundle card.
 * Removes every cart line of that bundle in one /cart/update.js call, then hands the response to Horizon the
 * same way the theme's own cart actions do: a CartUpdateEvent carrying the re-rendered sections, which
 * cart-items-component (drawer and /cart) morphs in and the header cart icon uses for its count.
 *
 * Markup: <cart-bundle-remove data-keys="key1,key2"> <button ref="removeButton" on:click="/remove"> …
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} removeButton - The trash button.
 *
 * @extends {Component<Refs>}
 */
class CartBundleRemove extends Component {
  requiredRefs = ['removeButton'];

  #busy = false;

  async remove() {
    if (this.#busy) return;

    const keys = (this.dataset.keys || '').split(',').filter(Boolean);
    if (keys.length === 0) return;

    const cartItems = this.closest('cart-items-component');
    const sourceId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId ?? '' : '';

    /** @type {Set<string>} */
    const sections = new Set();
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sections.add(element.dataset.sectionId);
    }

    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of keys) updates[key] = 0;

    const body = JSON.stringify({
      updates,
      sections: Array.from(sections).join(','),
      sections_url: window.location.pathname,
    });

    this.#setBusy(true, cartItems);

    try {
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const cart = await response.json();

      if (!response.ok || cart.errors || cart.status) {
        this.#showError(cart.description || cart.message || cart.errors);
        this.#setBusy(false, cartItems);
        return;
      }

      // Same event shape as component-cart-items.js; listeners re-render the drawer from `sections`.
      this.dispatchEvent(
        new CartUpdateEvent(cart, sourceId, {
          itemCount: cart.item_count,
          source: 'cart-bundle-remove',
          sections: cart.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.#showError();
      this.#setBusy(false, cartItems);
    } finally {
      cartItems?.classList.remove('cart-items-disabled');
    }
  }

  /**
   * @param {boolean} busy
   * @param {Element | null} cartItems
   */
  #setBusy(busy, cartItems) {
    this.#busy = busy;
    this.refs.removeButton.disabled = busy;
    this.closest('tr')?.classList.toggle('cart-items__bundle--removing', busy);
    cartItems?.classList.toggle('cart-items-disabled', busy);
  }

  /**
   * @param {string} [message]
   */
  #showError(message) {
    const container = this.closest('tr')?.querySelector('[data-bundle-error]');
    const text = container?.querySelector('.cart-item__error-text');
    if (!(container instanceof HTMLElement) || !(text instanceof HTMLElement)) return;
    text.textContent = typeof message === 'string' && message ? message : text.dataset.fallback ?? '';
    container.classList.remove('hidden');
  }
}

if (!customElements.get('cart-bundle-remove')) {
  customElements.define('cart-bundle-remove', CartBundleRemove);
}
